// Match Integrity Service — detects mismatches between stored song data and platform URL metadata
// Uses Odesli API to resolve URLs and compares returned metadata against stored title/artist

import { resolveFromUrl } from './odesliApi';
import { getSongByTrackId } from './songSearch';
import {
  firstFailedSave,
  saveSongLinks,
  type IntegrityStatus,
  type LibraryLink,
  type LibrarySong,
  type LinkProvider,
} from './songMatchMemory';
import { normalizeForComparison, stringSimilarity } from './requests';

// ============================================
// CONSTANTS
// ============================================

/** Below this similarity threshold, flag as mismatch (matches Desktop Helper's threshold) */
const MISMATCH_THRESHOLD = 0.8;

/** Delay between batch Odesli calls (~14 req/min, safely under 15/min limit) */
const ODESLI_BATCH_DELAY_MS = 4200;

// ============================================
// TYPES
// ============================================

export interface IntegrityCheckResult {
  integrityStatus: IntegrityStatus;
  resolvedTitle: string | null;
  resolvedArtist: string | null;
  titleSimilarity: number | null;
  artistSimilarity: number | null;
  checkError: string | null;
}

export interface BatchCheckResult {
  checked: number;
  mismatches: number;
  errors: number;
}

export interface BatchCheckItem {
  song: LibrarySong;
  provider: LinkProvider;
}

// ============================================
// CORE COMPARISON
// ============================================

function computeSimilarity(
  storedTitle: string,
  storedArtist: string,
  resolvedTitle: string,
  resolvedArtist: string,
): IntegrityCheckResult {
  const normStoredTitle = normalizeForComparison(storedTitle);
  const normResolvedTitle = normalizeForComparison(resolvedTitle);
  const normStoredArtist = normalizeForComparison(storedArtist);
  const normResolvedArtist = normalizeForComparison(resolvedArtist);

  const titleSim = stringSimilarity(normStoredTitle, normResolvedTitle);
  // If resolved artist is empty (e.g. Spotify oEmbed fallback), skip artist comparison
  const artistSim = normResolvedArtist ? stringSimilarity(normStoredArtist, normResolvedArtist) : 1;

  // Mismatch if either title OR artist falls below threshold
  const isMismatch = titleSim < MISMATCH_THRESHOLD || artistSim < MISMATCH_THRESHOLD;

  return {
    integrityStatus: isMismatch ? 'mismatch' : 'matched',
    resolvedTitle,
    resolvedArtist,
    titleSimilarity: Math.round(titleSim * 100) / 100,
    artistSimilarity: Math.round(artistSim * 100) / 100,
    checkError: null,
  };
}

// ============================================
// SINGLE CHECK
// ============================================

/**
 * Compare stored song title/artist against what the URL actually resolves to.
 * Uses Odesli API (1 API call) + optional iTunes lookup for enrichment.
 */
export async function checkVersionIntegrity(
  storedTitle: string,
  storedArtist: string,
  link: LibraryLink,
  appleId: string | null,
): Promise<IntegrityCheckResult> {
  try {
    // Step 1: Resolve the URL via Odesli
    const resolved = await resolveFromUrl(link.url);

    if (!resolved || !resolved.metadata) {
      // Try iTunes fallback if the song has an Apple ID
      if (appleId) {
        const itunesResult = await getSongByTrackId(Number(appleId), 'apple_music');
        if (itunesResult) {
          return computeSimilarity(
            storedTitle, storedArtist,
            itunesResult.trackName, itunesResult.artistName,
          );
        }
      }
      return {
        integrityStatus: 'error',
        resolvedTitle: null,
        resolvedArtist: null,
        titleSimilarity: null,
        artistSimilarity: null,
        checkError: resolved ? 'No metadata returned from URL' : 'URL resolution failed',
      };
    }

    // Step 2: Compare
    return computeSimilarity(
      storedTitle, storedArtist,
      resolved.metadata.title, resolved.metadata.artistName,
    );
  } catch (err) {
    return {
      integrityStatus: 'error',
      resolvedTitle: null,
      resolvedArtist: null,
      titleSimilarity: null,
      artistSimilarity: null,
      checkError: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Check and persist integrity for one player's link on a library song.
 * Returns the result. Saves through the saveSongLinks callable; throws if the save is refused.
 */
export async function checkAndPersistIntegrity(
  song: LibrarySong,
  provider: LinkProvider,
): Promise<IntegrityCheckResult> {
  const link = song.links[provider];
  if (!link) throw new Error(`No ${provider} link saved for this song`);
  const result = await checkVersionIntegrity(song.title, song.artist, link, song.appleId);

  const failure = firstFailedSave(await saveSongLinks([{
    songKey: song.key,
    link: link.url,
    integrity: {
      integrityStatus: result.integrityStatus,
      resolvedTitle: result.resolvedTitle,
      resolvedArtist: result.resolvedArtist,
      titleSimilarity: result.titleSimilarity,
      artistSimilarity: result.artistSimilarity,
      lastCheckedAt: new Date().toISOString(),
      checkError: result.checkError,
    },
  }]));
  if (failure) throw new Error(failure);

  return result;
}

// ============================================
// BATCH CHECK
// ============================================

/**
 * Batch check integrity for multiple library links with rate limiting.
 * Calls onProgress after each check. Cancellable via AbortSignal.
 */
export async function batchCheckIntegrity(
  matches: BatchCheckItem[],
  onProgress: (checked: number, total: number, current: string) => void,
  signal?: AbortSignal,
): Promise<BatchCheckResult> {
  let checked = 0;
  let mismatches = 0;
  let errors = 0;

  for (const { song, provider } of matches) {
    if (signal?.aborted) break;

    onProgress(checked, matches.length, `${song.title} - ${song.artist}`);

    try {
      const result = await checkAndPersistIntegrity(song, provider);
      if (result.integrityStatus === 'mismatch') mismatches++;
      if (result.integrityStatus === 'error') errors++;
    } catch (err) {
      console.warn('[matchIntegrity] Check failed for', song.key, provider, err);
      errors++;
    }

    checked++;

    // Rate limit: wait between calls (except after the last one)
    if (checked < matches.length && !signal?.aborted) {
      await new Promise(resolve => setTimeout(resolve, ODESLI_BATCH_DELAY_MS));
    }
  }

  onProgress(checked, matches.length, 'Done');
  return { checked, mismatches, errors };
}

// ============================================
// INLINE CHECK (no API call)
// ============================================

/**
 * Quick inline check using already-resolved metadata.
 * No API call needed — used in SongMatchModal where resolved data is already available.
 */
export function quickInlineCheck(
  storedTitle: string,
  storedArtist: string,
  resolvedTitle: string,
  resolvedArtist: string,
): { isMismatch: boolean; titleSim: number; artistSim: number } {
  const result = computeSimilarity(storedTitle, storedArtist, resolvedTitle, resolvedArtist);
  return {
    isMismatch: result.integrityStatus === 'mismatch',
    titleSim: result.titleSimilarity ?? 0,
    artistSim: result.artistSimilarity ?? 0,
  };
}
